import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import InnerPageLayout from "../../../components/feature/InnerPageLayout";
import BlogCard from "../components/BlogCard";
import { blogPosts } from "../../../mocks/blog";

const categoryColor: Record<string, { text: string; bg: string; border: string }> = {
  "비즈니스 전략": { text: "text-brand-purple", bg: "bg-brand-purple/10", border: "border-brand-purple/30" },
  "수익화": { text: "text-brand-teal", bg: "bg-brand-teal/10", border: "border-brand-teal/30" },
  "마케팅": { text: "text-brand-orange", bg: "bg-brand-orange/10", border: "border-brand-orange/30" },
  "법무·세금": { text: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  "성공 사례": { text: "text-pink-400", bg: "bg-pink-400/10", border: "border-pink-400/30" },
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const post = useMemo(() => blogPosts.find((p) => p.slug === slug), [slug]);
  const related = useMemo(
    () => blogPosts.filter((p) => p.slug !== slug && (p.category === post?.category || p.tags.some((t) => post?.tags.includes(t)))).slice(0, 3),
    [slug, post]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (post) {
      document.title = `${post.title} | 아르트레너 블로그`;
      const metaDesc = document.querySelector("meta[name='description']");
      if (metaDesc) metaDesc.setAttribute("content", post.excerpt);
    }
  }, [post]);

  if (!post) {
    return (
      <InnerPageLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6 px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl" style={{ background: "rgba(168,85,247,0.1)" }}>
            <i className="ri-article-line text-2xl text-brand-purple" />
          </div>
          <p className="text-white/50 font-body text-base">글을 찾을 수 없어요.</p>
          <button
            onClick={() => navigate("/blog")}
            className="px-6 py-3 rounded-full text-sm font-display font-medium text-white bg-brand-purple/20 border border-brand-purple/30 hover:bg-brand-purple/30 transition-all cursor-pointer whitespace-nowrap"
          >
            블로그 목록으로
          </button>
        </div>
      </InnerPageLayout>
    );
  }

  const color = categoryColor[post.category] ?? { text: "text-white/60", bg: "bg-white/10", border: "border-white/20" };

  return (
    <InnerPageLayout>
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.coverImage,
            "datePublished": post.publishedAt,
            "author": {
              "@type": "Person",
              "name": post.author.name,
              "jobTitle": post.author.role
            },
            "publisher": {
              "@type": "Organization",
              "name": "ARTREPRENEUR",
              "logo": "https://public.readdy.ai/ai/img_res/b51260fb-bf77-436f-865d-1c38bc3c656b.png"
            },
            "keywords": post.tags.join(", ")
          })
        }}
      />

      {/* Cover Image */}
      <div className="relative w-full h-64 md:h-96 lg:h-[480px] overflow-hidden">
        <img src={post.coverImage} alt={post.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(6,8,15,0.3) 0%, rgba(6,8,15,0.7) 100%)" }} />
      </div>

      {/* Article Container */}
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 lg:py-16">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-white/30 font-body mb-8" aria-label="breadcrumb">
          <a href="/" className="hover:text-white/60 transition-colors cursor-pointer">홈</a>
          <i className="ri-arrow-right-s-line" />
          <a href="/blog" className="hover:text-white/60 transition-colors cursor-pointer">블로그</a>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/50 truncate">{post.title}</span>
        </nav>

        {/* Category & Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className={`inline-flex text-xs font-display font-semibold tracking-widest uppercase px-3 py-1 rounded-full border ${color.text} ${color.bg} ${color.border}`}>
            {post.category}
          </span>
          <span className="text-white/30 text-xs font-body">{post.publishedAt.replace(/-/g, ".")}</span>
          <span className="text-white/30 text-xs font-body">·</span>
          <span className="text-white/30 text-xs font-body">{post.readTime}분 읽기</span>
        </div>

        {/* Title */}
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-snug lg:leading-relaxed mb-6">
          {post.title}
        </h1>

        {/* Excerpt */}
        <p className="text-white/60 font-body text-base leading-loose mb-8 pb-8 border-b border-white/8">
          {post.excerpt}
        </p>

        {/* Author */}
        <div className="flex items-center gap-4 mb-10 p-5 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
          <img src={post.author.avatar} alt={post.author.name} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0" />
          <div>
            <p className="font-display font-semibold text-white text-sm">{post.author.name}</p>
            <p className="text-white/40 text-xs font-body mt-0.5">{post.author.role}</p>
          </div>
        </div>

        {/* Article Content */}
        <div
          className="prose-artrepreneur"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-white/5">
          {post.tags.map((tag) => (
            <span key={tag} className="text-xs px-3 py-1.5 rounded-full font-body text-white/40 border border-white/10" style={{ background: "rgba(255,255,255,0.03)" }}>
              # {tag}
            </span>
          ))}
        </div>

        {/* Share */}
        <div className="flex items-center gap-4 mt-8 pt-8 border-t border-white/5">
          <span className="text-xs text-white/30 font-body">공유하기</span>
          {[
            { icon: "ri-twitter-x-line", label: "Twitter" },
            { icon: "ri-kakao-talk-fill", label: "KakaoTalk" },
            { icon: "ri-link", label: "링크 복사" },
          ].map((s) => (
            <button
              key={s.label}
              aria-label={s.label}
              className="w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-white transition-all duration-200 cursor-pointer"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <i className={`${s.icon} text-sm`} />
            </button>
          ))}
        </div>
      </article>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="px-4 md:px-6 pb-20 lg:pb-32" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="max-w-7xl mx-auto pt-14 lg:pt-20">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white mb-8 leading-snug lg:leading-relaxed">
              관련 아티클
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {related.map((relPost) => (
                <BlogCard key={relPost.id} post={relPost} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="px-4 md:px-6 pb-24 lg:pb-32">
        <div className="max-w-2xl mx-auto text-center">
          <div
            className="rounded-3xl p-10 lg:p-14"
            style={{ background: "linear-gradient(135deg, rgba(168,85,247,0.12), rgba(20,241,217,0.06))", border: "1px solid rgba(168,85,247,0.2)" }}
          >
            <span className="inline-block text-xs font-display font-medium tracking-widest uppercase text-brand-purple mb-5 px-3 py-1 rounded-full border border-brand-purple/30 bg-brand-purple/5">
              아르트레너 진단
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-4 leading-snug lg:leading-relaxed">
              글만 읽지 말고,<br />직접 시작해보세요.
            </h3>
            <p className="text-white/50 font-body text-sm mb-8 leading-loose">
              아르트레너 빌더팀과 1:1 진단을 통해 당신만의 비즈니스 로드맵을 만들어드립니다.
            </p>
            <a
              href="/#canvas"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-display font-semibold text-white text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #A855F7, #7C3AED)", boxShadow: "0 0 24px rgba(168,85,247,0.3)" }}
            >
              <i className="ri-flashlight-line" />
              무료 아르트레너 진단 받기
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .prose-artrepreneur h2 {
          font-family: var(--font-display, sans-serif);
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          line-height: 1.5;
        }
        .prose-artrepreneur h3 {
          font-family: var(--font-display, sans-serif);
          font-size: 1.15rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          margin-top: 1.75rem;
          margin-bottom: 0.75rem;
        }
        .prose-artrepreneur p {
          color: rgba(255,255,255,0.6);
          font-size: 0.9375rem;
          line-height: 1.9;
          margin-bottom: 1.2rem;
        }
        .prose-artrepreneur ul, .prose-artrepreneur ol {
          color: rgba(255,255,255,0.6);
          padding-left: 1.5rem;
          margin-bottom: 1.2rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .prose-artrepreneur li {
          font-size: 0.9375rem;
          line-height: 1.8;
        }
        .prose-artrepreneur strong {
          color: rgba(255,255,255,0.9);
          font-weight: 600;
        }
        .prose-artrepreneur blockquote {
          border-left: 3px solid rgba(168,85,247,0.5);
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          background: rgba(168,85,247,0.06);
          border-radius: 0 12px 12px 0;
        }
        .prose-artrepreneur blockquote p {
          color: rgba(255,255,255,0.7);
          font-style: italic;
          margin-bottom: 0.5rem;
        }
      `}</style>
    </InnerPageLayout>
  );
}
